import React, { useEffect } from "react"
import { QuarterlyReport } from "~/api/quarterly_reports"
import {
  Communication,
  ALWAYS_ENABLED_SECTIONS,
  VALID_SECTIONS,
} from "~/api/communications"
import { Editor } from "@/editor/Editor"
import {
  ALL_EDITOR_TOOLS,
  getChartToolData,
  getAiContentData,
} from "@/editor/editorConfig"
import logoName from "images/logo_name.svg"
import PageHeader from "./PageHeader"
import PageFooter from "./PageFooter"
import USMap from "./USMap"
import { GeographicExposureTable } from "@/editor/GeographicExposureTableTool"
import { PortfolioCompanyLogos } from "@/editor/PortfolioCompanyLogosTool"

interface PdfViewProps {
  reportData?: QuarterlyReport
  communicationData?: Communication
  sectionName?: string // If provided, render only this section
}

// Define section display names and their corresponding content keys
const sectionMapping = [
  {
    name: "LP High Level KPIs & Highlights",
    contentKey: "lp_high_level_kpis_and_highlights",
    hasEditor: true,
  },
  {
    name: "QPCSP Intro Letter",
    contentKey: "qpcsp_intro_letter",
    hasEditor: true,
  },
  {
    name: "LP Investment Performance",
    contentKey: "lp_investment_performance",
    hasEditor: true,
  },
  {
    name: "Attribution & Exposure Letter",
    contentKey: "attribution_exposure_letter",
    hasEditor: true,
  },
  {
    name: "Exposure by Sector",
    contentKey: "exposure_by_sector",
    hasEditor: true,
  },
  {
    name: "Exposure by Geography",
    contentKey: "exposure_by_geography",
    hasEditor: true,
  },
  {
    name: "Logos / Company Counts",
    contentKey: "logos_company_counts",
    hasEditor: true,
  },
  {
    name: "Historical Investment Performance",
    contentKey: "historical_investment_performance",
    hasEditor: true,
  },
  {
    name: "LP Capital Statement",
    contentKey: "lp_capital_statement",
    hasEditor: false,
  },
  {
    name: "QPCSP Financial Statement",
    contentKey: "qpcsp_financial_statement",
    hasEditor: false,
  },
]

const PdfView: React.FC<PdfViewProps> = ({
  reportData,
  communicationData,
  sectionName,
}) => {
  // Signal to Grover that rendering is complete
  useEffect(() => {
    // Set a flag on the window object that Grover can wait for
    const timer = setTimeout(() => {
      ;(window as any).pdfRenderComplete = true
      console.log("PDF render complete flag set")
    }, 1000) // Wait 1 second for everything to settle

    return () => clearTimeout(timer)
  }, [])

  if (!reportData) {
    return (
      <div className="alert alert-danger" role="alert">
        No report data provided for PDF generation
      </div>
    )
  }

  const report = reportData

  const enabledSections = report.sections || []
  const sectionContent = report.section_content || {}

  // Determine which sections to render
  let sectionsToRender: typeof sectionMapping

  if (sectionName) {
    // For individual section previews, render the requested section regardless of enabled status
    sectionsToRender = sectionMapping.filter(
      (section) => section.name === sectionName,
    )
  } else {
    // For full report, only render enabled sections
    sectionsToRender = sectionMapping.filter((section) =>
      enabledSections.includes(section.name),
    )
  }

  const chartToolData = getChartToolData(report)
  const aiContentData = getAiContentData(report)

  const coverLetterTitle = (() => {
    if (communicationData?.year && communicationData?.quarter) {
      return `${report.household_name} - Q${communicationData.quarter} ${communicationData.year}`
    }
    return report.household_name
  })()

  // Filter out Cover Letter and Table of Contents from the TOC display
  const tocSections = VALID_SECTIONS.filter((s) => enabledSections.includes(s))

  return (
    <div id="react-pdf-ready" className="pdf-render">
      {/* Cover Letter - Always First */}
      {(!sectionName || sectionName === "Cover Letter") && (
        <div className="pdf-render-section">
          <div className="pdf-render-content cover-letter-content">
            <img src={logoName} alt="Quadrant" className="cover-letter-logo" />
            <h1 className="cover-letter-title">{coverLetterTitle}</h1>
          </div>
        </div>
      )}

      {/* Table of Contents - Always Second */}
      {(!sectionName || sectionName === "Table of Contents") && (
        <div className="pdf-render-section">
          <div className="pdf-render-content pdf-render-content-centered">
            <h1 className="pdf-section-title">Table of Contents</h1>
            <ol className="table-of-contents-list">
              {tocSections.map((section) => {
                // Create anchor-friendly ID from section name
                const sectionId = section
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                return (
                  <li key={section} className="table-of-contents-item">
                    <a href={`#${sectionId}`}>{section}</a>
                  </li>
                )
              })}
            </ol>
          </div>
        </div>
      )}

      {sectionsToRender.map((section) => {
        // For editor sections, only render if there's actual content
        if (section.hasEditor) {
          // Exposure by Geography renders directly from data as two separate pages:
          // page 1 = section title + map, page 2 = geographic exposure table.
          // This bypasses EditorJS to avoid unpredictable page breaks with the 600px canvas.
          if (section.name === "Exposure by Geography") {
            const geoData =
              report.section_content?.editor_data_content
                ?.geographic_exposure_data
            const sectionId = section.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
            return (
              <React.Fragment key={section.contentKey}>
                <div id={sectionId} className="pdf-render-section">
                  <div className="pdf-render-content">
                    <h1 className="pdf-section-title">{section.name}</h1>
                    <h3 className="pdf-page-section-title">
                      Geographic Exposure
                    </h3>
                    {geoData && geoData.length > 0 ? (
                      <GeographicExposureTable data={geoData} />
                    ) : (
                      <div className="editorjs-empty-tool-table">
                        No table data available
                      </div>
                    )}
                  </div>
                </div>
                <div className="pdf-render-section">
                  <div className="pdf-render-content">
                    <h3 className="pdf-page-section-title">
                      Exposure by Geography
                    </h3>
                    {geoData && geoData.length > 0 ? (
                      <USMap data={geoData} />
                    ) : (
                      <div className="editorjs-empty-tool-table">
                        No geographic data available
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            )
          }

          // Logos / Company Counts renders directly from portfolio company data to avoid
          // unpredictable page breaks caused by EditorJS rendering the logos grid.
          if (section.name === "Logos / Company Counts") {
            const portfolioCompanies =
              report.section_content?.editor_data_content?.portfolio_companies
            const sectionId = section.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
            return (
              <div
                key={section.contentKey}
                id={sectionId}
                className="pdf-render-section"
              >
                <div className="pdf-render-content">
                  <h1 className="pdf-section-title">{section.name}</h1>
                  <h3 className="pdf-page-section-title">
                    Portfolio Companies
                  </h3>
                  {portfolioCompanies && portfolioCompanies.length > 0 ? (
                    <PortfolioCompanyLogos
                      portfolioCompanies={portfolioCompanies}
                    />
                  ) : (
                    <div className="editorjs-empty-tool-table">
                      No portfolio companies available
                    </div>
                  )}
                </div>
              </div>
            )
          }

          // QPCSP Intro Letter content lives at the template level (QuarterlyReport),
          // not per-household. Use communicationData as the authoritative source.
          if (section.name === "QPCSP Intro Letter") {
            const introLetterContent =
              communicationData?.intro_letter_editorjs_content
            const introLetterTitle =
              communicationData?.year && communicationData?.quarter
                ? `QPCSP ${communicationData.year} Q${communicationData.quarter}`
                : "QPCSP Intro Letter"

            const sectionId = section.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
            return (
              <div
                key={section.contentKey}
                id={sectionId}
                className="pdf-render-section"
              >
                <div className="pdf-render-content intro-letter-content">
                  <h1 className="intro-letter-title">{introLetterTitle}</h1>
                  {introLetterContent && (
                    <Editor
                      initialData={introLetterContent}
                      onChange={() => {}} // Read-only for PDF
                      reportId={`${report.id}-pdf-${section.contentKey}`}
                      readOnly={true}
                      enabledTools={[...ALL_EDITOR_TOOLS]}
                      chartToolData={chartToolData}
                      aiContentData={aiContentData}
                    />
                  )}
                </div>
              </div>
            )
          }

          const editorContent =
            sectionContent[section.contentKey]?.editor_content
          if (!editorContent) {
            return null // Skip sections without editor content
          }

          const sectionId = section.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
          return (
            <div
              key={section.contentKey}
              id={sectionId}
              className="pdf-render-section"
            >
              <div className="pdf-render-content">
                <h1 className="pdf-section-title">{section.name}</h1>
                <Editor
                  initialData={editorContent}
                  onChange={() => {}} // Read-only for PDF
                  reportId={`${report.id}-pdf-${section.contentKey}`}
                  readOnly={true}
                  enabledTools={[...ALL_EDITOR_TOOLS]}
                  chartToolData={chartToolData}
                  aiContentData={aiContentData}
                />
              </div>
            </div>
          )
        }

        // Non-editor sections (LP Capital Statement, QPCSP Financial Statement)
        // These will be handled differently in the future - skip for now
        return null
      })}
    </div>
  )
}

export default PdfView
